import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import gql from 'graphql-tag';
import useMediaQuery from '@mui/material/useMediaQuery';
import Link from '../Link';
import Moment from 'react-moment';
import { Avatar } from '@mui/material';
import { getImageUrl, rruleToText } from '../../utils/utils';
import StyledBoxOnHover from '../animated/StyledBoxOnHover';
import Favorite from '../../components/Favorite';
import Image from 'next/image';
import moment from 'moment';
import { RRule } from 'rrule';

export const ADD_FAVORITE = gql`
  mutation addFavoriteEvent($eventId: Int!, $userId: Int!, $favorite: Boolean!) {
    addFavoriteEvent(eventId: $eventId, userId: $userId, favorite: $favorite)
  }
`;

interface Picture {
  logo?: boolean;
  originalPicturePath?: string;
}

interface Entry {
  parentEntry?: { color?: string };
  icon?: string;
}

interface ParentEvent {
  url: string;
  label: string;
}

interface Event {
  id: number | string;
  url: string;
  label: string;
  shortDescription?: string;
  startedAt: number;
  endedAt: number;
  duration?: string;
  dateRule?: string;
  address?: string;
  city?: string;
  entries?: Entry[];
  pictures?: Picture[];
  parentEvent?: ParentEvent;
}

interface EventCardProps {
  event: Event;
}

const useStyles = makeStyles<{ color: string; icon: string }>()((theme, { color, icon }) => ({
  '@media print': {
    card: {
      border: 'solid 1px grey',
    },
    favorite: {
      display: 'none !important',
    },
  },
  card: {
    backgroundColor: 'white',
    [theme.breakpoints.up('sm')]: {
      minHeight: '120px',
      width: '100%',
    },
    [theme.breakpoints.down('md')]: {
      minHeight: '80px',
      width: '100%',
    },
    display: 'flex',
    justifyContent: 'space-between',
  },
  content: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexGrow: 1,
    [theme.breakpoints.up('sm')]: {
      margin: '0 24px',
    },
    [theme.breakpoints.down('md')]: {
      margin: '0 5px',
      width: '85%',
    },
  },
  favorite: {
    width: '10%',
    borderLeft: `dashed 2px ${color}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      cursor: 'pointer',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  favoriteIcon: {
    color: color,
  },
  category: {
    width: 87,
    height: 107,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    marginLeft: 'auto',
    [theme.breakpoints.down('md')]: {
      marginRight: 8,
      width: 30,
    },
  },
  opacity: {
    width: '40px',
    height: '40px',
    position: 'absolute',
    borderRadius: '50%',
    backgroundColor: color,
    opacity: '0.3',
  },
  categoryIcon: {
    width: '40px',
    height: '40px',
    mask: `url('/icons/${icon}.svg')`,
    maskPosition: 'center center',
    maskRepeat: 'no-repeat',
    maskSize: '22px',
    background: color,
  },
  leftContent: {
    display: 'flex',
    alignItems: 'center',
  },
  logo: {
    minHeight: 72,
    minWidth: 72,
    marginLeft: 12,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    borderRadius: '50%',
    [theme.breakpoints.down('md')]: {
      minHeight: 60,
      minWidth: 60,
      marginLeft: 8,
    },
  },
  image: {
    width: '72px!important',
    height: '72px!important',
    position: 'relative!Important' as 'relative',
    borderRadius: '50%',
  },
  contentText: {
    overflow: 'hidden',
    marginLeft: 12,
    [theme.breakpoints.down('md')]: {
      marginLeft: 8,
    },
  },
  label: {
    fontWeight: 'bold',
    color: color,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    fontSize: '0.9em!important',
  },
  eventDetails: {
    fontStyle: 'italic',
    color: '#A3A3A3',
    fontSize: '0.9em',
  },
  avatar: {
    backgroundColor: 'white',
  },
  link: {},
}));

const EventCard = ({ event }: EventCardProps) => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  const color = event?.entries?.[0]?.parentEntry?.color || '#AD2740';
  const icon = event?.entries?.[0]?.icon || 'fruit';
  const { classes } = useStyles({ color, icon });

  const startDateFormat = matches ? '[De ]HH[h]mm' : 'HH[h]mm';
  const endDateFormat = matches ? '[ à ]HH[h]mm' : '[-]HH[h]mm';

  const logoPath = useMemo(() => {
    const logoPaths = (event?.pictures || []).filter((picture) => picture.logo);
    if (logoPaths.length > 0) {
      return getImageUrl(logoPaths[0].originalPicturePath);
    }
    return null;
  }, [event]);

  const addressCity = useMemo(() => {
    if (!event.city && !event.address) return 'Adresse manquante';
    const list = [event.address, event.city];
    return `${list.join(', ')}`;
  }, [event.address, event.city]);

  const recurrenceText = useMemo(() => {
    if (!event.dateRule) return null;
    try {
      const startDate = moment(parseInt(String(event.startedAt)));
      const rrule = RRule.fromString(`DTSTART:${startDate.format('YYYYMMDD[T]HHmmss[Z]')}\nRRULE:${event.dateRule}`);
      const text = rruleToText(rrule);
      const timeFormat = startDate.format('HH[h]mm');
      return `${text} à ${timeFormat}`;
    } catch (error) {
      console.error('Error parsing recurrence rule:', error);
      return null;
    }
  }, [event.dateRule, event.startedAt]);

  return (
    <StyledBoxOnHover className={classes.card}>
      <div className={classes.content}>
        <Link href={`/evenement/${event.url}`} target="_blank" color="inherit" underline="none" width="100%" className={classes.link}>
          <div className={classes.leftContent}>
            {logoPath && (
              <Avatar
                className={classes.avatar}
                sx={{
                  width: 72,
                  height: 72,
                }}
              >
                <Image
                  src={logoPath}
                  alt={event.label}
                  fill
                  style={{ objectFit: 'contain' }}
                />
              </Avatar>
            )}
            <div className={classes.contentText}>
              <div className={classes.label}>{event.label}</div>
              <div className={classes.eventDetails}>
                {!event.duration && !event.dateRule && (
                  <>
                    <Moment format={startDateFormat} unix>
                      {event.startedAt / 1000}
                    </Moment>
                    <Moment format={endDateFormat} unix>
                      {event.endedAt / 1000}
                    </Moment>
                    <span> - </span>
                  </>
                )}
                {!event.duration && event.dateRule && recurrenceText && (
                  <>
                    <span>{recurrenceText}</span>
                    <span> - </span>
                  </>
                )}
                <span>{addressCity}</span>
                <br />
                <span>{event.shortDescription}</span>
              </div>
              {event.duration && (
                <>
                  {event.duration}
                  {' '}
                  .
                </>
              )}
              {event.parentEvent && (
                <span>
                  Fait partie de
                  {' '}
                  <Link href={`/evenement/${event.parentEvent.url}`}>{event.parentEvent.label}</Link>
                </span>
              )}
            </div>
          </div>
        </Link>
      </div>
      <div className={classes.category}>
        <span className={classes.opacity} />
        <span className={classes.categoryIcon} />
      </div>
      <div className={classes.favorite}>
        <Favorite event={event} />
      </div>
    </StyledBoxOnHover>
  );
};

export default EventCard;
